import { AlertTriangle, Globe, Mail } from "lucide-react";
import { useState } from "react";

import { ErrorAlert, cx, Modal, Button } from "@/components/ui";
import type { DnsChange, DnsPreview } from "@/lib/domains";

import { usesPriority } from "./util";

const CHANGE_LABEL = { kept: "Kept", new: "New", removed: "Removed", changed: "Changed" } as const;

/** Figma 4724-7245 "Review DNS change": the diff, the conflicts, the impact, then apply. */
export function ReviewModal({
  open,
  fqdn,
  change,
  preview,
  onBack,
  onClose,
  onConfirm,
  applying,
  error,
}: {
  open: boolean;
  fqdn: string;
  change: DnsChange;
  preview: DnsPreview;
  onBack: () => void;
  onClose: () => void;
  onConfirm: () => void;
  applying: boolean;
  error: unknown;
}) {
  const [understood, setUnderstood] = useState(false);
  const website = preview.impacts.find((i) => i.area === "website");
  const email = preview.impacts.find((i) => i.area === "email");
  const needsAck = Boolean(website?.affected || email?.affected);
  const ack =
    website?.affected && email?.affected
      ? `I understand this can change where visitors to ${fqdn} are sent and where its email is delivered.`
      : email?.affected
        ? `I understand this can change where email for ${fqdn} is delivered.`
        : `I understand this can change where visitors to ${fqdn} are sent.`;
  const verb = change.op === "add" ? "Add" : change.op === "update" ? "Change" : "Remove";
  const hasPriority = preview.rows.some((r) => usesPriority(r.record.type));

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      title="Review DNS change"
      subtitle={`Check what changes before anything is applied to ${fqdn}.`}
      note="Unrelated records are kept. Changes show as Pending until the DNS provider confirms."
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onBack}>
            Back to edit
          </Button>
          <Button
            variant="brand"
            size="lg"
            loading={applying}
            disabledReason={
              needsAck && !understood ? "Tick the box to confirm you understand the impact" : undefined
            }
            onClick={onConfirm}
          >
            Confirm and apply
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-border bg-[#fefaf8] px-3 py-2.5 text-[13px] text-fg">
        <span className="rounded-md bg-[#221c19] px-1.5 py-0.5 text-[11px] font-semibold text-white">
          {change.type}
        </span>
        <span className="min-w-0 break-all">
          {verb} {change.name} → {change.content}
          {usesPriority(change.type ?? "") && change.priority !== null && change.priority !== undefined
            ? ` · priority ${change.priority}`
            : ""}
          {change.op !== "delete" && change.ttl ? ` · TTL ${change.ttl}` : ""}
        </span>
      </div>

      {(preview.conflicts.length > 0 || preview.warnings.length > 0) && (
        <div className="flex gap-2.5 rounded-xl border border-[#ffc4bb] bg-[#fff1ee] p-3.5">
          <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-[#d6342f]" />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-[13px] font-semibold text-fg">
              {preview.conflicts.length
                ? `Conflicts with existing ${preview.conflicts[0]!.name === "@" ? fqdn : preview.conflicts[0]!.name} records`
                : "Check this before applying"}
            </p>
            {[...preview.conflicts.map((c) => c.message), ...preview.warnings].map((m) => (
              <p key={m} className="text-xs text-[#5c5551]">
                {m}
              </p>
            ))}
          </div>
        </div>
      )}

      {preview.rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[440px] text-left text-xs">
            <thead className="bg-[#faf6ef] text-[10.5px] tracking-wide text-[#766e6a] uppercase">
              <tr>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Host</th>
                <th className="px-3 py-2 font-medium">Value</th>
                {hasPriority && <th className="px-3 py-2 font-medium">Priority</th>}
                <th className="px-3 py-2 font-medium">Change</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row) => {
                const isNew = row.change === "new" || row.change === "changed";
                const removed = row.change === "removed";
                return (
                  <tr
                    key={`${row.change}-${row.record.id}`}
                    className={cx("border-t border-border", isNew && "bg-[#fef4e5] text-[#995c05]")}
                  >
                    <td className="px-3 py-2.5">{row.record.type}</td>
                    <td className="px-3 py-2.5">{row.record.name}</td>
                    <td
                      className={cx(
                        "px-3 py-2.5 font-medium break-all",
                        removed && "text-[#b52522] line-through",
                      )}
                    >
                      {row.change === "changed" &&
                        row.previous &&
                        row.previous.content !== row.record.content && (
                          <span className="block font-normal text-fg-subtle line-through">
                            {row.previous.content}
                          </span>
                        )}
                      {row.record.content}
                    </td>
                    {hasPriority && <td className="px-3 py-2.5">{row.record.priority ?? "—"}</td>}
                    <td
                      className={cx(
                        "px-3 py-2.5 whitespace-nowrap",
                        removed ? "text-[#b52522]" : isNew ? "font-medium" : "text-[#5c5551]",
                      )}
                    >
                      {CHANGE_LABEL[row.change]}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        <p className="text-[13px] font-semibold text-fg">What this affects</p>
        {[email, website].map(
          (impact) =>
            impact && (
              <div key={impact.area} className="flex gap-2">
                {impact.area === "email" ? (
                  <Mail
                    aria-hidden
                    className={cx(
                      "mt-0.5 size-4 shrink-0",
                      impact.affected ? "text-[#d6342f]" : "text-[#007955]",
                    )}
                  />
                ) : (
                  <Globe
                    aria-hidden
                    className={cx(
                      "mt-0.5 size-4 shrink-0",
                      impact.affected ? "text-[#d6342f]" : "text-[#007955]",
                    )}
                  />
                )}
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-fg">
                    {impact.area === "email" ? "Email" : "Website"}
                  </p>
                  <p className="text-xs text-fg-subtle">{impact.message}</p>
                </div>
              </div>
            ),
        )}
      </div>

      {needsAck && (
        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border p-3.5 text-[13px] text-fg">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-brand-500"
          />
          {ack}
        </label>
      )}
      <ErrorAlert error={error} />
    </Modal>
  );
}
